import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Users, Activity, FileText, BarChart3, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

export const PatientSatisfactionCaseStudy = () => {
  const navigate = useNavigate();

  // Simulated descriptive statistics
  const descriptiveStats = [
    { variable: "Doctor Communication", mean: 3.8, sd: 0.9, median: 4, range: "1-5" },
    { variable: "Staff Behavior", mean: 3.9, sd: 0.8, median: 4, range: "1-5" },
    { variable: "Cleanliness", mean: 4.1, sd: 0.7, median: 4, range: "1-5" },
    { variable: "Billing Experience", mean: 3.4, sd: 1.1, median: 3, range: "1-5" },
    { variable: "Appointment Ease", mean: 3.6, sd: 1.0, median: 4, range: "1-5" },
    { variable: "Overall Satisfaction", mean: 3.7, sd: 0.9, median: 4, range: "1-5" }
  ];

  // Reliability analysis
  const reliabilityData = {
    cronbachAlpha: 0.89,
    items: 8,
    interpretation: "Excellent internal consistency"
  };

  // Factor analysis results
  const factorLoadings = [
    { item: "Doctor Communication", factor1: 0.82, factor2: 0.15 },
    { item: "Staff Behavior", factor1: 0.79, factor2: 0.21 },
    { item: "Cleanliness", factor1: 0.18, factor2: 0.84 },
    { item: "Billing Experience", factor1: 0.24, factor2: 0.76 },
    { item: "Appointment Ease", factor1: 0.71, factor2: 0.32 }
  ];

  // Statistical test results
  const testResults = [
    { 
      test: "ANOVA: Overall Satisfaction by Department", 
      statistic: "F(3, 846) = 12.4", 
      pValue: "< 0.001", 
      interpretation: "Significant difference"
    },
    { 
      test: "Chi-square: Complaints by Satisfaction Level", 
      statistic: "χ²(4) = 89.3", 
      pValue: "< 0.001", 
      interpretation: "Strong association"
    },
    { 
      test: "ANOVA: Satisfaction by Waiting Time", 
      statistic: "F(2, 847) = 31.7", 
      pValue: "< 0.001", 
      interpretation: "Significant difference"
    }
  ];

  // Regression results
  const regressionResults = [
    { predictor: "Doctor Communication", coefficient: 0.52, se: 0.04, tValue: 13.2, pValue: "< 0.001", beta: 0.48 },
    { predictor: "Staff Behavior", coefficient: 0.31, se: 0.05, tValue: 6.4, pValue: "< 0.001", beta: 0.29 },
    { predictor: "Cleanliness", coefficient: 0.18, se: 0.05, tValue: 3.6, pValue: "< 0.001", beta: 0.17 },
    { predictor: "Waiting Time (High)", coefficient: -0.45, se: 0.08, tValue: -5.6, pValue: "< 0.001", beta: -0.21 },
    { predictor: "Billing Experience", coefficient: 0.25, se: 0.04, tValue: 6.1, pValue: "< 0.001", beta: 0.23 }
  ];

  // Department satisfaction data
  const deptSatisfaction = [
    { dept: "Emergency", mean: 3.4, n: 198 },
    { dept: "Outpatient", mean: 3.9, n: 312 },
    { dept: "Surgery", mean: 3.6, n: 189 },
    { dept: "Maternity", mean: 4.1, n: 151 }
  ];

  // Waiting time vs satisfaction
  const waitingSatisfaction = [
    { category: "< 30 min", mean: 4.2, q1: 4, median: 4, q3: 5 },
    { category: "30-60 min", mean: 3.7, q1: 3, median: 4, q3: 4 },
    { category: "> 60 min", mean: 3.1, q1: 3, median: 3, q3: 4 }
  ];

  const tools = [
    "R Statistical Software",
    "SPSS",
    "Questionnaire Design",
    "Factor Analysis",
    "Reliability Testing",
    "Regression Modeling",
    "Healthcare Analytics",
    "Survey Data Analysis"
  ];

  const keyInsights = [
    "Doctor-patient communication quality demonstrated the strongest influence on overall satisfaction (standardized β=0.48, p&lt;0.001), highlighting the critical role of effective clinical communication in patient experience",
    "The questionnaire showed excellent psychometric properties with Cronbach's α=0.89, confirming high internal consistency and reliability for measuring patient satisfaction constructs",
    "Waiting time emerged as a significant negative predictor (β=-0.21, p&lt;0.001), with patients experiencing waits &gt;60 minutes reporting satisfaction scores 0.45 points lower on average",
    "Emergency department showed significantly lower satisfaction (M=3.4) compared to Maternity (M=4.1), F(3,846)=12.4, p&lt;0.001, suggesting need for targeted quality interventions",
    "Factor analysis revealed two distinct dimensions: 'Interpersonal Care Quality' (staff and doctor communication) and 'System Efficiency' (billing, cleanliness, appointments), explaining 68% of variance"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-blue-50 to-white">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/case-studies')}
            className="flex items-center space-x-2 text-teal-600 hover:text-teal-700 mb-6 transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Case Studies</span>
          </button>

          <Badge className="bg-teal-100 text-teal-700 mb-4 border-teal-200">
            Healthcare Quality · Survey Analysis · Patient Experience · Hospital Analytics
          </Badge>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Is Actually Driving Your Patient Satisfaction Scores. And What Is Quietly Dragging Them Down
          </h1>

          <p className="text-xl text-gray-600 mb-6 leading-relaxed">
            A rigorous statistical analysis of 850 patient responses that identified the specific drivers of satisfaction. and turned survey data into a ranked quality improvement roadmap.
          </p>

          <div className="flex flex-wrap gap-2">
            {["Survey Analysis", "Factor Analysis", "Regression Modeling", "Healthcare Quality"].map((tag, index) => (
              <span key={index} className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm border border-gray-200">
                {tag}
              </span>
            ))}
          </div>

          {/* Simulated Data Disclaimer */}
          <div className="mt-6 bg-yellow-500/20 border border-yellow-300/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-900 font-medium">Simulated Data for Demonstration</p>
                <p className="text-xs text-yellow-800 mt-1">
                  All patient satisfaction data and statistical results are synthetically generated for portfolio demonstration purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* At a Glance */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">At a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Situation</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A healthcare organisation was collecting patient satisfaction data across four departments and multiple branches. but had no statistical framework to identify which specific aspects of care were actually driving overall satisfaction, or which were quietly pulling scores down.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What We Did</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Conducted a full biostatistical analysis of 850 patient responses using reliability testing, exploratory factor analysis, ANOVA, chi-square testing, and multivariate regression. producing a ranked, evidence-based list of exactly what to fix and in what order.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What It Showed</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Doctor communication was the single strongest predictor of overall satisfaction, explaining 48% of the variation in scores. Waiting times above 60 minutes caused a statistically significant 0.45-point drop. Billing experience had the lowest score and the highest variability. the most inconsistent patient experience in the organisation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Situation */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Situation</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              A healthcare organisation operating across multiple departments. Emergency, Outpatient, Surgery, and Maternity. was running a patient satisfaction survey programme collecting 850 responses over a 12-month period. The data existed. The problem was that nobody was doing anything meaningful with it.
            </p>
            <p>
              Department managers received summary scores. Leadership saw overall averages. But nobody had a statistically validated answer to the question that actually matters: which specific aspects of our care are driving satisfaction up. and which are driving it down?
            </p>
            <p>
              Without that answer, quality improvement initiatives were generic. Resources were spread across everything rather than concentrated on the two or three levers that would actually move the needle.
            </p>
            <p className="font-semibold text-gray-900">
              This engagement replaced intuition with evidence.
            </p>
          </div>
        </div>
      </section>

      {/* The Business Problem */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Business Problem</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed mb-6">
            <p>
              Patient satisfaction in UAE private healthcare is not just a quality metric. It is a retention metric, a referral metric, and increasingly a regulatory metric as DHA and DOH frameworks place greater weight on patient experience indicators.
            </p>
            <p>
              The financial logic is straightforward. A dissatisfied patient does not return. They do not refer. In a market where acquiring a new patient costs significantly more than retaining an existing one, every point on a satisfaction scale has a patient retention implication.
            </p>
            <p>
              The problem this organisation faced was not a lack of data. it was a lack of analysis rigorous enough to tell them where to act:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Overall satisfaction averaged 3.7 out of 5. above the midpoint but with significant room for improvement</li>
              <li>28% of patients gave a neutral response (score of 3). a persuadable group that targeted improvement could shift toward satisfaction</li>
              <li>18.9% of patients reported active dissatisfaction (scores 1–2). a retention risk that was not being addressed systematically</li>
              <li>Satisfaction varied significantly across departments but without statistical testing nobody knew whether those differences were meaningful or random variation</li>
            </ul>
          </div>
          
          <Card className="border-2 border-amber-400 bg-amber-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">The question nobody had answered:</h3>
              <p className="text-gray-700 leading-relaxed">
                Which specific aspects of care. doctor communication, staff behaviour, cleanliness, billing, waiting time. actually predict whether a patient will be satisfied overall? And by how much does each one matter relative to the others? Without a regression model, you cannot answer this. Without the answer, you cannot prioritise.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Where Satisfaction Was Breaking Down */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Where Satisfaction Was Breaking Down</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Before any improvement strategy can be built, you need to know exactly where the gaps are. which aspects of care are underperforming, which departments are the problem, and whether the differences are statistically real or just noise. This is what the analysis established first.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Area</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Mean Score (out of 5)</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Key Finding</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">What This Means</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Doctor Communication</td>
                  <td className="px-4 py-4 text-sm text-gray-700">3.8 / 5</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Highest correlation with overall satisfaction (r=0.78). Strongest regression predictor (β=0.48, p&lt;0.001)</td>
                  <td className="px-4 py-4 text-sm text-gray-700">The single highest-return improvement target in the organisation. A one-point improvement in communication scores predicts a 0.52-point improvement in overall satisfaction. holding all other factors constant</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Staff Behaviour</td>
                  <td className="px-4 py-4 text-sm text-gray-700">3.9 / 5</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Second strongest predictor of overall satisfaction (β=0.29, p&lt;0.001). Strong correlation with doctor communication (r=0.68)</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Frontline staff interactions substantially influence patient perceptions beyond clinical care quality. Communication and staff behaviour together form the Interpersonal Care Quality factor that dominates patient experience</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Billing Experience</td>
                  <td className="px-4 py-4 text-sm text-gray-700">3.4 / 5 (lowest of all items)</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Highest variability of any measured item (SD=1.1). the most inconsistent patient experience in the organisation. Significant regression predictor (β=0.23, p&lt;0.001)</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Billing is where patient experience is most inconsistent and most frequently poor. This is an operational fix with direct patient retention implications. and the easiest win in the System Efficiency factor</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Waiting Time</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Not rated directly. measured as a service variable</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Significant negative predictor (β=-0.21, p&lt;0.001). Patients waiting &gt;60 minutes scored satisfaction 0.45 points lower on average. Patients waiting &lt;30 minutes scored 4.2 vs 3.1 for those waiting &gt;60 minutes</td>
                  <td className="px-4 py-4 text-sm text-gray-700">A 1.1-point satisfaction gap between short and long wait patients. the largest single operational variable affecting scores. Waiting time reduction is a concrete, measurable, high-return quality improvement target</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Department Variation</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Emergency 3.4 / Outpatient 3.9 / Surgery 3.6 / Maternity 4.1</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Significant variation across departments (F(3,846)=12.4, p&lt;0.001). differences are statistically real, not random</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Generic hospital-wide interventions will not work. Each department needs a specific strategy. Emergency and Surgery require targeted attention while Maternity practices should be studied as the internal benchmark</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-sm text-gray-600 italic mt-4">
            All figures are directly from the statistical analysis of 850 patient responses. SD = Standard Deviation. β = standardised regression coefficient indicating relative importance when controlling for all other factors.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction & Business Problem</h2>
          <Card className="border-l-4 border-teal-500">
            <CardContent className="p-6">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Patient satisfaction is a critical indicator of healthcare quality, directly influencing 
                clinical outcomes, patient retention, hospital reputation, and reimbursement metrics. 
                In value-based care models, understanding drivers of patient experience has become 
                essential for both quality improvement and financial sustainability.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Despite widespread use of patient satisfaction surveys, many healthcare organizations 
                struggle to extract actionable insights from questionnaire data. Without rigorous 
                statistical analysis, it remains unclear which specific aspects of care most strongly 
                influence overall satisfaction, how satisfaction varies across departments and patient 
                populations, and whether survey instruments reliably measure the constructs of interest.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Objectives */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Research Objectives</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { 
                icon: TrendingUp, 
                title: "Evaluate Patient Satisfaction", 
                desc: "Quantify satisfaction levels across hospital departments and branches" 
              },
              { 
                icon: BarChart3, 
                title: "Identify Key Drivers", 
                desc: "Determine which factors most strongly predict overall satisfaction using multivariate modeling" 
              },
              { 
                icon: Users, 
                title: "Compare Across Groups", 
                desc: "Test for significant differences in satisfaction by department, branch, and patient demographics" 
              },
              { 
                icon: Activity, 
                title: "Validate Survey Instrument", 
                desc: "Assess reliability and construct validity of the satisfaction questionnaire" 
              }
            ].map((obj, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <obj.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{obj.title}</h3>
                  <p className="text-gray-600">{obj.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Dataset Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Dataset Description</h2>
          <Card>
            <CardContent className="p-6">
              <p className="text-lg text-gray-700 mb-4">
                <strong>Sample Size:</strong> 850 patient responses
              </p>
              <p className="text-lg text-gray-700 mb-6">
                <strong>Data Collection Period:</strong> January 2024 – December 2024
              </p>
              <p className="text-lg text-gray-700 mb-6">
                <strong>Survey Design:</strong> 5-point Likert scale (1 = Very Dissatisfied, 5 = Very Satisfied)
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Variables Collected</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { category: "Identifiers", vars: "Patient ID, Hospital Branch, Department, Visit Type" },
                  { category: "Demographics", vars: "Age Group, Gender" },
                  { category: "Service Variables", vars: "Waiting Time Category" },
                  { category: "Satisfaction Items (Likert 1-5)", vars: "Doctor Communication, Staff Behavior, Cleanliness, Billing Experience, Appointment Ease" },
                  { category: "Outcome Variables", vars: "Overall Satisfaction (Likert 1-5), Recommend Hospital (Binary), Complaint Flag (Binary)" }
                ].map((item, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{item.category}</h4>
                    <p className="text-sm text-gray-600">{item.vars}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Statistical Methods */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold text-teal-600 mb-2">THE ANALYSIS</p>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Found It. Statistical Methodology</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Five complementary statistical methods were applied sequentially. each answering a specific question that the previous method could not. This is what rigorous survey analysis looks like when it is designed to produce actionable outputs rather than just descriptive statistics.
          </p>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Descriptive Statistics</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Computed means, standard deviations, medians, and ranges for all Likert-scale items 
                    to characterize central tendency and variability in patient satisfaction ratings.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Reliability Analysis (Cronbach's Alpha)</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Assessed internal consistency of the satisfaction scale using Cronbach's alpha coefficient. 
                    Values ≥ 0.70 indicate acceptable reliability, ≥ 0.80 indicate good reliability, and ≥ 0.90 
                    indicate excellent reliability.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Exploratory Factor Analysis (EFA)</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Conducted principal axis factoring with varimax rotation to identify underlying latent 
                    constructs. Factor loadings &gt; 0.40 considered meaningful. Extracted factors explain the 
                    shared variance among satisfaction items.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Inferential Testing</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    <strong>One-way ANOVA:</strong> Tested for significant differences in mean satisfaction 
                    scores across departments and waiting time categories. Post-hoc Tukey HSD tests identified 
                    specific group differences.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Chi-square Test:</strong> Examined associations between categorical variables 
                    (e.g., complaint flag and satisfaction level).
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Multiple Linear Regression</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Modeled overall satisfaction as the dependent variable with satisfaction items, waiting 
                    time, and department as predictors. Standardized beta coefficients (β) indicate the 
                    relative importance of each predictor when controlling for others.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Statistical Significance:</strong> All tests used α = 0.05 significance level. 
                  P-values &lt; 0.05 indicate statistical significance.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Results - Descriptive Statistics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Results</h2>
          
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Descriptive Statistics</h3>
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Variable</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Mean</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">SD</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Median</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {descriptiveStats.map((stat, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">{stat.variable}</td>
                        <td className="text-center py-3 px-4 text-gray-900 font-medium">{stat.mean}</td>
                        <td className="text-center py-3 px-4 text-gray-700">{stat.sd}</td>
                        <td className="text-center py-3 px-4 text-gray-700">{stat.median}</td>
                        <td className="text-center py-3 px-4 text-gray-700">{stat.range}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Interpretation:</strong> Cleanliness received the highest mean rating (M=4.1, SD=0.7), 
                  indicating generally positive perceptions of facility hygiene. Billing experience showed the 
                  lowest mean (M=3.4, SD=1.1) with highest variability, suggesting this is a problematic area 
                  with inconsistent patient experiences. Overall satisfaction averaged 3.7 (SD=0.9), slightly 
                  above the midpoint, indicating room for improvement across the patient experience continuum.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Reliability Analysis */}
          <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-12">Reliability Analysis</h3>
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="text-4xl font-bold text-teal-600 mb-2">{reliabilityData.cronbachAlpha}</div>
                  <div className="text-sm font-medium text-gray-700">Cronbach's Alpha</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="text-4xl font-bold text-gray-900 mb-2">{reliabilityData.items}</div>
                  <div className="text-sm font-medium text-gray-700">Survey Items</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="text-lg font-semibold text-green-600 mb-2">{reliabilityData.interpretation}</div>
                  <div className="text-sm font-medium text-gray-700">Reliability Level</div>
                </div>
              </div>
              <div className="p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Interpretation:</strong> The Cronbach's alpha of 0.89 indicates excellent internal 
                  consistency reliability. This confirms that the satisfaction questionnaire items are 
                  measuring a cohesive underlying construct (patient satisfaction) with minimal measurement 
                  error. The scale demonstrates strong psychometric properties suitable for clinical quality 
                  research and performance monitoring. Values above 0.80 are considered ideal for healthcare 
                  survey instruments used in decision-making contexts.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Factor Analysis */}
          <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-12">Factor Analysis Results</h3>
          <Card className="mb-8">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                <strong>Extraction Method:</strong> Principal Axis Factoring | <strong>Rotation:</strong> Varimax | 
                <strong className="ml-2">Variance Explained:</strong> 68.3%
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Questionnaire Item</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Factor 1<br/>(Interpersonal)</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Factor 2<br/>(System)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factorLoadings.map((item, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">{item.item}</td>
                        <td className="text-center py-3 px-4">
                          <span className={item.factor1 > 0.40 ? "font-bold text-teal-600" : "text-gray-500"}>
                            {item.factor1.toFixed(2)}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={item.factor2 > 0.40 ? "font-bold text-blue-600" : "text-gray-500"}>
                            {item.factor2.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Interpretation:</strong> Factor analysis revealed two distinct latent dimensions:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Factor 1 - Interpersonal Care Quality:</strong> Doctor communication (0.82), 
                      staff behavior (0.79), and appointment ease (0.71) loaded strongly, representing 
                      human interaction aspects of care
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Factor 2 - System Efficiency:</strong> Cleanliness (0.84) and billing (0.76) 
                      loaded strongly, capturing operational and administrative dimensions
                    </span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  This two-factor structure suggests patient satisfaction is driven by both relational 
                  (interpersonal) and transactional (system) components, each requiring distinct quality 
                  improvement strategies.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Statistical Tests */}
          <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-12">Inferential Statistical Tests</h3>
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Statistical Test</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Test Statistic</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">p-value</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Interpretation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((result, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">{result.test}</td>
                        <td className="text-center py-3 px-4 text-gray-900 font-mono text-xs">{result.statistic}</td>
                        <td className="text-center py-3 px-4 font-bold text-teal-600">{result.pValue}</td>
                        <td className="text-center py-3 px-4">
                          <Badge className="bg-green-100 text-green-700">{result.interpretation}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Interpretation:</strong>
                </p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Department Differences:</strong> ANOVA revealed significant variation in 
                      satisfaction across departments (F=12.4, p&lt;0.001). This indicates department-specific 
                      factors significantly impact patient experience, necessitating targeted interventions 
                      rather than hospital-wide generic approaches.
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Complaints Association:</strong> Chi-square test showed strong association 
                      between complaint filing and low satisfaction (χ²=89.3, p&lt;0.001), validating that 
                      formal complaints are reliable markers of patient dissatisfaction requiring immediate 
                      quality response.
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Waiting Time Impact:</strong> ANOVA demonstrated significant effect of waiting 
                      time on satisfaction (F=31.7, p&lt;0.001), confirming wait times as a critical operational 
                      metric affecting patient perceptions of care quality.
                    </span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Regression Results */}
          <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-12">Multiple Regression Analysis</h3>
          <Card className="mb-8">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                <strong>Dependent Variable:</strong> Overall Satisfaction | 
                <strong className="ml-4">Model R² = 0.76</strong> | 
                <strong className="ml-4">Adjusted R² = 0.74</strong> | 
                <strong className="ml-4">F(8, 841) = 289.3, p &lt; 0.001</strong>
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Predictor</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">B</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">SE</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">t-value</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">p-value</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">β (Std)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regressionResults.map((result, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">{result.predictor}</td>
                        <td className="text-center py-3 px-4 text-gray-900 font-mono text-xs">{result.coefficient.toFixed(2)}</td>
                        <td className="text-center py-3 px-4 text-gray-700 font-mono text-xs">{result.se.toFixed(2)}</td>
                        <td className="text-center py-3 px-4 text-gray-900 font-mono text-xs">{result.tValue.toFixed(1)}</td>
                        <td className="text-center py-3 px-4 font-bold text-teal-600">{result.pValue}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-bold ${Math.abs(result.beta) > 0.30 ? 'text-teal-600' : 'text-gray-700'}`}>
                            {result.beta.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Interpretation:</strong> The regression model explained 74% of variance in overall 
                  satisfaction (Adjusted R²=0.74), indicating excellent predictive power.
                </p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Strongest Predictor:</strong> Doctor communication (β=0.48, p&lt;0.001) emerged as 
                      the most influential factor. A one-point increase in communication ratings predicts a 
                      0.52-point increase in overall satisfaction, holding other factors constant. This highlights 
                      physician-patient interaction as the primary driver of care experience.
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Staff Behavior:</strong> Second strongest predictor (β=0.29, p&lt;0.001), demonstrating 
                      that frontline staff interactions substantially influence patient perceptions beyond clinical care.
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Waiting Time:</strong> High waiting times showed significant negative effect 
                      (β=-0.21, p&lt;0.001), reducing satisfaction by 0.45 points. This validates waiting time 
                      reduction as a concrete, measurable quality improvement target.
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>Billing Experience:</strong> Moderate predictor (β=0.23, p&lt;0.001), indicating 
                      administrative processes affect overall care perceptions, suggesting need for billing system improvements.
                    </span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Regression Model Insight Callout */}
          <Card className="mt-8 border-2 border-teal-400 bg-teal-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What the Regression Model Tells You That a Simple Average Cannot</h3>
              <p className="text-gray-700 leading-relaxed">
                The regression model explains 74% of the variance in overall satisfaction scores (Adjusted R²=0.74). This means that knowing a patient&apos;s ratings on doctor communication, staff behaviour, cleanliness, waiting time, and billing experience allows you to predict their overall satisfaction score with 74% accuracy. More importantly, the standardised coefficients (β) tell you the relative importance of each factor when controlling for all the others. so you know not just that doctor communication matters, but that it matters more than twice as much as cleanliness when deciding where to direct quality improvement resources.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Visualizations */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Supporting Visualizations</h2>

          {/* Satisfaction by Department */}
          <Card className="mb-8 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mean Satisfaction Score by Department</h3>
              <p className="text-sm text-gray-600 mb-6">Comparison of patient satisfaction across hospital departments (n=850)</p>
              
              {/* Y-axis labels and chart container */}
              <div className="flex gap-4">
                {/* Y-axis */}
                <div className="flex flex-col justify-between text-xs text-gray-600 pt-8 pb-8">
                  <span>5.0</span>
                  <span>4.0</span>
                  <span>3.0</span>
                  <span>2.0</span>
                  <span>1.0</span>
                </div>
                
                {/* Chart area */}
                <div className="flex-1">
                  <div className="space-y-6">
                    {deptSatisfaction.map((dept, index) => {
                      const barWidth = (dept.mean / 5) * 100;
                      const colors = [
                        'from-red-500 to-red-600',
                        'from-teal-500 to-teal-600',
                        'from-blue-500 to-blue-600',
                        'from-purple-500 to-purple-600'
                      ];
                      return (
                        <div key={index} className="relative">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-gray-900 min-w-[100px]">{dept.dept}</span>
                            <span className="text-sm text-gray-600">(n={dept.n})</span>
                          </div>
                          {/* Grid lines */}
                          <div className="absolute inset-0 flex justify-between pointer-events-none" style={{ left: 0, right: 0 }}>
                            {[20, 40, 60, 80, 100].map((pos, i) => (
                              <div key={i} className="w-px bg-gray-200 h-10" style={{ left: `${pos}%` }}></div>
                            ))}
                          </div>
                          <div className="h-10 bg-gray-100 rounded-lg overflow-hidden relative border border-gray-200">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index]} flex items-center justify-end pr-4 transition-all duration-700 shadow-sm`}
                              style={{ width: `${barWidth}%` }}
                            >
                              <span className="text-sm font-bold text-white drop-shadow-md">{dept.mean.toFixed(1)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* X-axis labels */}
                  <div className="flex justify-between text-xs text-gray-600 mt-2 px-1">
                    <span>1.0</span>
                    <span>2.0</span>
                    <span>3.0</span>
                    <span>4.0</span>
                    <span>5.0</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Significance:</strong> Maternity department shows highest satisfaction (4.1), 
                  likely reflecting positive emotional context of childbirth. Emergency department's lower score 
                  (3.4) may reflect acute care stressors, system pressures, and patient acuity. These departmental 
                  differences suggest need for context-specific improvement strategies rather than uniform interventions.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Waiting Time Box Plot */}
          <Card className="mb-8 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Satisfaction Distribution by Waiting Time Category</h3>
              <p className="text-sm text-gray-600 mb-6">Box plots showing median, quartiles, and range of satisfaction scores</p>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                {/* Chart with grid */}
                <div className="relative">
                  {/* Horizontal grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {[5, 4, 3, 2, 1].map((val, i) => (
                      <div key={i} className="flex items-center">
                        <span className="text-xs text-gray-500 w-8 -ml-10">{val}</span>
                        <div className="flex-1 border-t border-gray-300 border-dashed"></div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-end justify-around gap-8 h-72 relative pt-4">
                    {waitingSatisfaction.map((wait, index) => {
                      const boxHeight = ((wait.median - 1) / 4) * 100;
                      const q1Height = ((wait.q1 - 1) / 4) * 100;
                      const q3Height = ((wait.q3 - 1) / 4) * 100;
                      const colors = ['bg-green-500', 'bg-yellow-500', 'bg-red-500'];
                      const borderColors = ['border-green-700', 'border-yellow-700', 'border-red-700'];
                      
                      return (
                        <div key={index} className="flex flex-col items-center flex-1 max-w-[120px]">
                          <div className="text-base font-bold text-gray-900 mb-2">{wait.mean.toFixed(1)}</div>
                          <div className="relative w-full" style={{ height: '220px' }}>
                            {/* Upper whisker */}
                            <div 
                              className="absolute left-1/2 w-0.5 bg-gray-600"
                              style={{ 
                                bottom: `${q3Height}%`, 
                                height: `${100 - q3Height}%`,
                                transform: 'translateX(-50%)'
                              }}
                            />
                            {/* Box */}
                            <div 
                              className={`absolute bottom-0 w-full ${colors[index]} ${borderColors[index]} border-2 rounded shadow-md`}
                              style={{ 
                                height: `${q3Height - q1Height}%`,
                                bottom: `${q1Height}%`
                              }}
                            >
                              {/* Median line */}
                              <div 
                                className="absolute w-full h-1 bg-gray-900"
                                style={{ bottom: `${((wait.median - wait.q1) / (wait.q3 - wait.q1)) * 100}%` }}
                              />
                            </div>
                            {/* Lower whisker */}
                            <div 
                              className="absolute left-1/2 w-0.5 bg-gray-600"
                              style={{ 
                                bottom: 0,
                                height: `${q1Height}%`,
                                transform: 'translateX(-50%)'
                              }}
                            />
                            {/* Whisker caps */}
                            <div className="absolute left-1/4 right-1/4 h-0.5 bg-gray-600" style={{ bottom: 0 }} />
                            <div className="absolute left-1/4 right-1/4 h-0.5 bg-gray-600" style={{ bottom: '100%' }} />
                          </div>
                          <div className="text-sm font-semibold text-gray-900 mt-3 text-center">{wait.category}</div>
                          <div className="text-xs text-gray-600">waiting time</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Significance:</strong> Clear dose-response relationship between waiting time 
                  and satisfaction. Patients waiting &lt;30 minutes show median satisfaction of 4.0, declining to 
                  3.0 for those waiting &gt;60 minutes. This 1-point difference represents substantial clinical 
                  significance in patient experience. Reducing wait times from "high" to "moderate" category 
                  could improve satisfaction scores by approximately 0.6 points across the patient population.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Correlation Heatmap */}
          <Card className="mb-8 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Correlation Matrix of Satisfaction Items</h3>
              <p className="text-sm text-gray-600 mb-6">Pearson correlation coefficients (r) showing relationships between survey items</p>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-7 gap-2 min-w-[600px]">
                    {/* Headers */}
                    <div className="text-xs font-bold text-gray-900"></div>
                    {["Doctor", "Staff", "Clean", "Billing", "Appt", "Overall"].map((label, i) => (
                      <div key={i} className="text-xs font-bold text-gray-900 text-center py-2">{label}</div>
                    ))}
                    
                    {/* Correlation cells */}
                    {[
                      ["Doctor", [1.00, 0.68, 0.41, 0.35, 0.59, 0.78]],
                      ["Staff", [0.68, 1.00, 0.52, 0.38, 0.55, 0.71]],
                      ["Clean", [0.41, 0.52, 1.00, 0.61, 0.44, 0.58]],
                      ["Billing", [0.35, 0.38, 0.61, 1.00, 0.48, 0.52]],
                      ["Appt", [0.59, 0.55, 0.44, 0.48, 1.00, 0.64]],
                      ["Overall", [0.78, 0.71, 0.58, 0.52, 0.64, 1.00]]
                    ].map(([label, values], rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <div className="text-xs font-bold text-gray-900 flex items-center py-2">{label}</div>
                        {values.map((value, colIndex) => {
                          const bgColor = value >= 0.7 ? 'bg-teal-600' : 
                                         value >= 0.5 ? 'bg-teal-400' : 
                                         value >= 0.3 ? 'bg-teal-200' : 'bg-gray-200';
                          const textColor = value >= 0.5 ? 'text-white' : 'text-gray-900';
                          const borderClass = rowIndex === colIndex ? 'border-2 border-gray-700' : 'border border-gray-300';
                          return (
                            <div key={colIndex} className={`${bgColor} ${textColor} ${borderClass} text-xs font-semibold p-3 text-center rounded shadow-sm hover:shadow-md transition-shadow`}>
                              {value.toFixed(2)}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                
                {/* Legend */}
                <div className="mt-6 flex items-center justify-center gap-6 text-xs flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-teal-600 rounded border border-gray-300"></div>
                    <span className="text-gray-700">Strong (≥0.7)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-teal-400 rounded border border-gray-300"></div>
                    <span className="text-gray-700">Moderate (0.5-0.69)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-teal-200 rounded border border-gray-300"></div>
                    <span className="text-gray-700">Weak (0.3-0.49)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-200 rounded border border-gray-300"></div>
                    <span className="text-gray-700">Very weak (&lt;0.3)</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Significance:</strong> Doctor communication shows strongest correlation with 
                  overall satisfaction (r=0.78), followed by staff behavior (r=0.71). These high correlations 
                  validate the regression findings and confirm interpersonal aspects dominate patient experience. 
                  Moderate correlation between cleanliness and billing (r=0.61) suggests patients view facility 
                  conditions and administrative processes as related system-level quality indicators.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Distribution */}
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Distribution of Overall Satisfaction Scores</h3>
              <p className="text-sm text-gray-600 mb-6">Frequency distribution showing response patterns (n=850)</p>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                {/* Chart with grid */}
                <div className="relative">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-600 -ml-12 h-48">
                    <span>350</span>
                    <span>280</span>
                    <span>210</span>
                    <span>140</span>
                    <span>70</span>
                    <span>0</span>
                  </div>
                  
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="border-t border-gray-300 border-dashed"></div>
                    ))}
                  </div>
                  
                  <div className="h-48 flex items-end justify-around gap-3 relative">
                    {[
                      { score: "1", count: 42, pct: 4.9, color: "from-red-500 to-red-600" },
                      { score: "2", count: 119, pct: 14.0, color: "from-orange-500 to-orange-600" },
                      { score: "3", count: 238, pct: 28.0, color: "from-yellow-500 to-yellow-600" },
                      { score: "4", count: 331, pct: 38.9, color: "from-green-500 to-green-600" },
                      { score: "5", count: 120, pct: 14.1, color: "from-teal-500 to-teal-600" }
                    ].map((data, index) => {
                      const barHeight = (data.count / 331) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center max-w-[100px]">
                          <div className="text-sm font-bold text-gray-900 mb-1">{data.count}</div>
                          <div 
                            className={`w-full bg-gradient-to-t ${data.color} rounded-t transition-all duration-700 hover:opacity-80 shadow-md border-b-4 border-gray-300`}
                            style={{ height: `${barHeight}%` }}
                          />
                          <div className="text-sm font-bold text-gray-900 mt-2">Score: {data.score}</div>
                          <div className="text-xs text-gray-600">({data.pct}%)</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* X-axis label */}
                <div className="mt-4 text-center text-sm font-semibold text-gray-700">
                  Overall Satisfaction Score (1 = Very Dissatisfied, 5 = Very Satisfied)
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Significance:</strong> Distribution shows positive skew with modal response 
                  at 4 (38.9% of responses). Only 18.9% reported dissatisfaction (scores 1-2), while 53.0% 
                  reported satisfaction (scores 4-5). This distribution is typical for healthcare satisfaction 
                  surveys, but the 28% neutral responses (score 3) represent a persuadable cohort where targeted 
                  improvements could shift perceptions toward satisfaction.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Key Findings */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Findings</h2>
          <Card className="bg-teal-50 border-teal-200">
            <CardContent className="p-6">
              <ul className="space-y-4">
                {keyInsights.map((insight, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 leading-relaxed">{insight}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What This Delivered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Delivered</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Survey data sitting in a spreadsheet has no value. The value is in the analysis that tells you what to fix, in what order, and why. This is what this engagement produced:
          </p>

          {/* Before vs After Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-red-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Before This Analysis</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">Overall satisfaction score of 3.7. no breakdown by driver or department</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">Quality improvement budget spread across generic initiatives with no evidence base</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">Billing complaints handled reactively with no data on how much they were affecting overall satisfaction</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">Waiting time targets set operationally. no connection to patient satisfaction data</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">Department managers could not distinguish real performance differences from random variation</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold mt-1">•</span>
                    <span className="text-gray-700">28% neutral patients. no strategy to convert them toward satisfaction</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">After This Analysis</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Ranked list of satisfaction drivers by statistical importance. doctor communication first, staff behaviour second, billing third</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Quality improvement roadmap prioritised by regression coefficients. highest-return interventions identified first</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Billing experience confirmed as lowest-scoring, highest-variability item. targeted operational improvement now evidence-based</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Waiting time above 60 minutes quantified as a 0.45-point satisfaction drag. reduction target now tied to a measurable patient experience outcome</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Department differences confirmed as statistically significant. Emergency and Surgery now have department-specific improvement plans</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">28% neutral patients identified as the highest-return conversion opportunity. targeted through communication and billing improvements</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Impact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">A Ranked Quality Improvement Roadmap</h3>
                <p className="text-gray-700 leading-relaxed">
                  The regression analysis produced something no satisfaction report had previously given this organisation. a ranked list of exactly which aspects of care to fix first, second, and third, based on their statistical contribution to overall satisfaction. Doctor communication (β=0.48) delivers more than twice the satisfaction return of cleanliness (β=0.17) per unit of improvement. This changes how quality improvement budgets should be allocated.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Waiting Time Target Is Now Evidence-Based</h3>
                <p className="text-gray-700 leading-relaxed">
                  Patients waiting less than 30 minutes scored 4.2 on satisfaction. Patients waiting more than 60 minutes scored 3.1. a 1.1-point gap representing the largest single operational lever available. Reducing high-wait patient volume from the &gt;60 minute category to the 30–60 minute category is predicted to improve satisfaction scores by approximately 0.6 points across the patient population. That target is now measurable and trackable.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Two Distinct Improvement Strategies. Not One</h3>
                <p className="text-gray-700 leading-relaxed">
                  Factor analysis revealed that patient satisfaction is driven by two distinct dimensions. Interpersonal Care Quality (doctor communication, staff behaviour, appointment ease) and System Efficiency (cleanliness, billing). These require completely different improvement strategies. Interpersonal quality is improved through training, communication protocols, and consultation structure. System efficiency is improved through operational process redesign. Treating them as one problem leads to unfocused interventions that move neither.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Survey Instrument Is Validated for Ongoing Use</h3>
                <p className="text-gray-700 leading-relaxed">
                  Cronbach&apos;s alpha of 0.89 confirms excellent internal consistency. the survey instrument is reliably measuring what it is designed to measure. This means the organisation can continue using the same survey with confidence that score changes over time reflect real changes in patient experience, not measurement noise. The psychometric foundation for a longitudinal patient satisfaction monitoring programme is now established.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services This Connects To */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Services Used in This Engagement</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">📋</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Survey & Questionnaire Data Analysis</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Survey design validation, Likert scale analysis, reliability testing, factor analysis, questionnaire data interpretation and reporting.
                </p>
                <Link to="/services#survey-analysis" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Learn more →
                </Link>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">🏥</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Clinical & Healthcare Analytics</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Patient outcome analysis and benchmarking, KPI dashboards for departments and management, DHA and DOH compliance support.
                </p>
                <Link to="/services#clinical-analytics" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Learn more →
                </Link>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">📊</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Outcome Optimization</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Performance benchmarking against internal targets, quality improvement strategy with measurable targets, identification of highest-return improvement opportunities.
                </p>
                <Link to="/services#outcome-optimization" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Learn more →
                </Link>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">📈</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Performance Monitoring & Dashboards</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Trend and time-series analysis, continuous monitoring systems for patient experience metrics, actionable reporting for clinical and management audiences.
                </p>
                <Link to="/services#performance-monitoring" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Learn more →
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What This Demonstrates */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Project Demonstrates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Comprehensive questionnaire data analysis methodology suitable for healthcare research",
              "Advanced biostatistical techniques: reliability testing, factor analysis, and multivariate regression",
              "Ability to extract actionable clinical insights from complex survey data",
              "Understanding of psychometric properties and survey instrument validation",
              "Healthcare quality improvement perspective integrating patient experience metrics",
              "Clear communication of statistical results to non-technical healthcare stakeholders"
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Skills */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tools & Skills Used</h2>
          <div className="flex flex-wrap gap-3">
            {tools.map((tool, index) => (
              <span key={index} className="px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-300">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Is Your Patient Satisfaction Data Telling You What You Think It Is?
          </h2>
          <p className="text-lg text-teal-100 mb-4">
            Most healthcare organisations collect satisfaction data. Very few analyse it rigorously enough to know which specific aspects of care are driving their scores. and which are quietly pulling them down. If you have survey data and a satisfaction score but no ranked list of what to fix first, you have the data without the insight.
          </p>
          <p className="text-lg text-teal-100 mb-8">
            A free 30-minute consultation is where we start. We look at what survey data you have, identify what analysis would produce the most actionable output, and show you what a quality improvement roadmap built on statistical evidence looks like.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-teal-600 hover:bg-gray-100 transition-colors duration-300" size="lg">
                Book a Free Consultation
              </Button>
            </Link>
            <Link to="/services">
              <Button className="bg-teal-500 text-white hover:bg-teal-400 transition-colors duration-300" size="lg">
                View Our Healthcare Analytics Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientSatisfactionCaseStudy;
